import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';
import {
  Wallet as WalletIcon, TrendingUp, Handshake, Trophy, Crown, Gift,
  Banknote, ArrowUpRight, ArrowDownRight, PiggyBank, Package, History, Star, PlusCircle
} from 'lucide-react';

const incomeIcons = { roi: TrendingUp, direct: Handshake, level: Trophy, royalty: Crown, reward: Gift, adjustment: Star };
const incomeColors = { roi: 'var(--primary)', direct: '#22c55e', level: '#3b82f6', royalty: '#a855f7', reward: '#f59e0b', adjustment: '#ec4899' };

const formatCurrency = (n) => `₹${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Wallet() {
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/income/summary'),
      api.get('/income/my?page=1&limit=8')
    ]).then(([summaryRes, incomeRes]) => {
      setSummary(summaryRes.data);
      setRecent(incomeRes.data.incomes || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading || !summary) {
    return <DashboardLayout><div className="loader-wrap"><div className="loader"></div></div></DashboardLayout>;
  }

  const walletCards = [
    { label: 'ROI Wallet', key: 'roiWallet', icon: TrendingUp, color: 'var(--primary)' },
    { label: 'Direct Wallet', key: 'directWallet', icon: Handshake, color: '#22c55e' },
    { label: 'Level Wallet', key: 'levelWallet', icon: Trophy, color: '#3b82f6' },
    { label: 'Royalty Wallet', key: 'royaltyWallet', icon: Crown, color: '#a855f7' },
    { label: 'Reward Wallet', key: 'rewardWallet', icon: Gift, color: '#f59e0b' },
    { label: 'Bonus Wallet', key: 'walletBalance', icon: Star, color: '#ec4899' },
  ];

  return (
    <DashboardLayout>
      <h1 className="page-title">Wallet</h1>
      <p className="page-subtitle">Your wallet balance and how much money has come into your account</p>

      {/* Hero: Withdrawable Balance + Deposit Wallet — ONE card, two sections
          (divided, not two separate boxes) so everything about "your money"
          lives in a single place at the top of the page. */}
      <div
        className="card"
        style={{
          marginBottom: 24,
          background: 'linear-gradient(135deg, rgba(var(--primary-rgb),0.12), rgba(34,197,94,0.08))',
          borderColor: 'rgba(var(--primary-rgb),0.3)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
          <div style={{
            width: 60, height: 60, borderRadius: '50%',
            background: 'rgba(var(--primary-rgb),0.15)', color: 'var(--primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>
            <PiggyBank size={30} />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>Wallet Balance (Withdrawable)</div>
            <div style={{ fontSize: '2.2rem', fontWeight: 700, color: 'var(--primary)', fontFamily: 'Rajdhani' }}>
              {formatCurrency(summary.totalWithdrawable)}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#22c55e', fontWeight: 700, fontSize: '1.1rem' }}>
                <ArrowUpRight size={16} /> {formatCurrency(summary.totalEarned)}
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Total Earned</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#ef4444', fontWeight: 700, fontSize: '1.1rem' }}>
                <ArrowDownRight size={16} /> {formatCurrency(summary.totalWithdrawn)}
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Total Withdrawn</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#3b82f6', fontWeight: 700, fontSize: '1.1rem' }}>
                {formatCurrency(summary.todayEarned)}
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Earned Today</div>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          <Link to="/dashboard/withdraw" className="btn btn-primary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Banknote size={14} /> Withdraw
          </Link>
          <Link to="/dashboard/package" className="btn btn-outline btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Package size={14} /> Buy / Renew Package
          </Link>
        </div>

        {/* Deposit Wallet — funds admin has approved for you, spendable ONLY
            on a package purchase. Same card as above (not a separate box),
            split off by a divider since it works differently: it goes UP
            when a deposit request is approved, and DOWN the instant you
            buy a package — never withdrawable. */}
        <div style={{ borderTop: '1px solid rgba(var(--primary-rgb),0.2)', marginTop: 20, paddingTop: 20, display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
          <div style={{
            width: 52, height: 52, borderRadius: '50%',
            background: 'rgba(59,130,246,0.15)', color: '#3b82f6',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>
            <PlusCircle size={26} />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>Deposit Wallet (for package purchase only — not withdrawable)</div>
            <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#3b82f6', fontFamily: 'Rajdhani' }}>
              {formatCurrency(summary.depositWallet)}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Link to="/dashboard/deposit" className="btn btn-outline btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <PlusCircle size={14} /> Add Funds
            </Link>
            <Link to="/dashboard/package" className="btn btn-primary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Package size={14} /> Buy Package
            </Link>
          </div>
        </div>
      </div>

      {/* Wallet-type breakdown (how much came in, by source) */}
      <div className="wallet-grid" style={{ marginBottom: 24 }}>
        {walletCards.map(w => (
          <div key={w.key} className="wallet-card">
            <div style={{ marginBottom: 6, color: w.color, display: 'flex', justifyContent: 'center' }}><w.icon size={24} /></div>
            <div className="wallet-label">{w.label}</div>
            <div className="wallet-value" style={{ color: w.color }}>{formatCurrency(summary[w.key])}</div>
          </div>
        ))}
      </div>

      {/* Active Package quick info */}
      {summary.activePackage > 0 && (
        <div className="alert alert-info" style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Package size={16} />
          <span>
            Active Package: <strong style={{ color: 'var(--primary)' }}>{formatCurrency(summary.activePackage)}</strong>
            {summary.packageExpiresAt && <> &nbsp;|&nbsp; Expires: {new Date(summary.packageExpiresAt).toLocaleDateString('en-IN')}</>}
          </span>
        </div>
      )}

      {/* Recent money-in transactions */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><History size={18} /> Recent Wallet Credits</h3>
          <Link to="/dashboard/income" style={{ color: 'var(--primary)', fontSize: '0.82rem', fontWeight: 600 }}>View Full History →</Link>
        </div>
        {recent.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon"><WalletIcon size={40} /></div>
            <h3>No money in your wallet yet</h3>
            <p style={{ fontSize: '0.85rem', marginTop: 6 }}>Activate a package to start earning.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="tp-table">
              <thead>
                <tr><th>Date</th><th>Type</th><th>Description</th><th>Amount</th></tr>
              </thead>
              <tbody>
                {recent.map(inc => {
                  const RowIcon = incomeIcons[inc.type];
                  return (
                    <tr key={inc._id}>
                      <td style={{ fontSize: '0.85rem' }}>{new Date(inc.createdAt).toLocaleDateString('en-IN')}</td>
                      <td>
                        <span className="badge" style={{ background: `${incomeColors[inc.type]}18`, color: incomeColors[inc.type], border: `1px solid ${incomeColors[inc.type]}40`, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {RowIcon && <RowIcon size={12} />} {inc.type.toUpperCase()}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.85rem' }}>{inc.description || '—'}</td>
                      <td style={{ color: '#22c55e', fontWeight: 700 }}>+{formatCurrency(inc.amount)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}